import type {
  TlxUserEntitlementAttributeValueResponseModel,
  UserAttributeValueColumn,
} from "@/api";
import { hashString } from "./generate";

/**
 * Demo stand-in for an agent's entitlement attributes (the Agent Attributes
 * tab on the preview agent details). The live tab reads
 * `GET /api/user_entitlement_attribute/value?identityType=AGENT`; demo agents
 * are not applications, so they read this store instead - through their own
 * mock-only methods, never the real endpoint, which the live create wizard
 * also calls inside the demo scope.
 *
 * In memory only: a reload restores the seeded values.
 */

const column = (
  name: string,
  displayName: string,
  extra: Partial<UserAttributeValueColumn> = {},
): UserAttributeValueColumn => ({
  id: `demo-attribute-${name.toLowerCase()}`,
  name,
  displayName,
  status: "SYNCED",
  type: "VARCHAR",
  actualDataType: "VARCHAR",
  isMultiValue: false,
  attributeType: "NORMAL_ATTRIBUTE",
  dataProduct: "ALL",
  attributeGroupName: "Default",
  readOnly: false,
  ...extra,
});

export const DEMO_AGENT_ATTRIBUTE_COLUMNS: UserAttributeValueColumn[] = [
  column("AGENT_USER_ID", "Agent User", {
    attributeType: "USER_NAME",
    description: "Session agent user identifier",
  }),
  column("AGENT_DEPARTMENT", "Agent Department", {
    description: "The department accountable for the agent.",
  }),
  column("TRUSTAIAGENTS_AGENTTIER", "Agent Tier", {
    description: "Synced from the agent registry; not editable here.",
    readOnly: true,
  }),
  column("COST_CENTER", "Cost Center"),
  column("DATA_CLASSIFICATIONS", "Data Classifications", {
    isMultiValue: true,
    description: "Classifications of the data the agent may read.",
  }),
  column("PRODUCTION_APPROVED", "Production Approved", {
    type: "BOOLEAN",
    actualDataType: "BOOLEAN",
  }),
];

const DEPARTMENTS = ["Finance", "Customer Support", "Engineering", "Legal"];
const TIERS = ["One", "Two", "Three"];
const CLASSIFICATIONS = [["Internal"], ["Confidential", "PII"], ["Public"]];

/** Stable per agent, so a demo reads the same on every visit. */
const pick = <T>(items: T[], agentId: string, salt: number): T =>
  items[hashString(`${agentId}:${salt}`) % items.length];

const seedValues = (agentId: string): Record<string, unknown> => ({
  AGENT_USER_ID: agentId,
  AGENT_DEPARTMENT: pick(DEPARTMENTS, agentId, 1),
  TRUSTAIAGENTS_AGENTTIER: pick(TIERS, agentId, 2),
  COST_CENTER: `CC-${(pick([1042, 2310, 3177, 4605], agentId, 3)).toString()}`,
  DATA_CLASSIFICATIONS: pick(CLASSIFICATIONS, agentId, 4),
  PRODUCTION_APPROVED: pick([true, false], agentId, 5),
});

class AgentAttributeMockStore {
  private values = new Map<string, Record<string, unknown>>();

  private valuesFor(agentId: string): Record<string, unknown> {
    if (!this.values.has(agentId)) this.values.set(agentId, seedValues(agentId));
    return this.values.get(agentId)!;
  }

  list({
    agentId,
  }: {
    agentId: string;
  }): TlxUserEntitlementAttributeValueResponseModel {
    return {
      columns: DEMO_AGENT_ATTRIBUTE_COLUMNS,
      data: [
        {
          id: `demo-attribute-record-${agentId}`,
          dataProduct: "ALL",
          values: { ...this.valuesFor(agentId) },
          status: "SYNCED",
          cascaded: false,
          lastModifiedDate: new Date(),
        },
      ],
      pageNo: 1,
      pageSize: 1,
      total: 1,
    };
  }

  /** Whole-record replace, like the real PATCH; read-only columns are kept. */
  update({
    agentId,
    values,
  }: {
    agentId: string;
    values: Record<string, unknown>;
  }): TlxUserEntitlementAttributeValueResponseModel {
    const current = this.valuesFor(agentId);
    const readOnly = DEMO_AGENT_ATTRIBUTE_COLUMNS.filter(
      item => item.readOnly && item.name,
    ).map(item => item.name as string);
    const next = { ...values };
    readOnly.forEach(name => {
      next[name] = current[name];
    });
    this.values.set(agentId, next);
    return this.list({ agentId });
  }

  reset() {
    this.values.clear();
  }
}

export const agentAttributeStore = new AgentAttributeMockStore();
